/**
 * The session token issued by `register/finish` / `login/finish`.
 *
 * Passkeys are the only credential, so this token is the *only* thing that
 * survives a reload — there is no client-generated user id to fall back on
 * (hexagon had one; an account-required product must not). Losing it means one
 * biometric tap, not a lost identity.
 */
import { apiUrl } from '../apiBase'

export interface Account {
  readonly userId: string
  readonly displayName: string
}

const TOKEN_KEY = 'cards.session'
const USER_KEY = 'cards.user'

function safeGet(key: string): string | null {
  try {
    return localStorage.getItem(key)
  } catch {
    return null
  }
}

function safeSet(key: string, value: string | null): void {
  try {
    if (value === null) localStorage.removeItem(key)
    else localStorage.setItem(key, value)
  } catch {
    /* private mode — the in-memory copy carries this tab only */
  }
}

let token: string | null = safeGet(TOKEN_KEY)
let account: Account | null = (() => {
  const raw = safeGet(USER_KEY)
  if (!raw) return null
  try {
    return JSON.parse(raw) as Account
  } catch {
    return null
  }
})()

export function getSessionToken(): string | null {
  return token
}

export function getAccount(): Account | null {
  return account
}

export function setSession(nextToken: string, nextAccount: Account): void {
  token = nextToken
  account = nextAccount
  safeSet(TOKEN_KEY, nextToken)
  safeSet(USER_KEY, JSON.stringify(nextAccount))
}

export function clearSession(): void {
  token = null
  account = null
  safeSet(TOKEN_KEY, null)
  safeSet(USER_KEY, null)
}

/** Every REST call goes through here so the bearer header is never forgotten. */
export async function api<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers)
  headers.set('Content-Type', 'application/json')
  if (token) headers.set('Authorization', `Bearer ${token}`)
  const res = await fetch(apiUrl(path), { ...init, headers })
  const text = await res.text()
  // A proxy, an SPA fallback or an outage answers with HTML, and a raw
  // "Unexpected token '<'" must never reach a player as copy.
  let body: unknown = null
  let parsed = false
  try {
    body = text ? (JSON.parse(text) as unknown) : null
    parsed = true
  } catch {
    parsed = false
  }
  if (!res.ok || !parsed) {
    const message =
      parsed && body && typeof body === 'object' && 'message' in body
        ? String((body as { message: unknown }).message)
        : `${res.status} ${res.statusText}`.trim()
    if (res.status === 401) clearSession()
    throw new Error(message)
  }
  return body as T
}
